type SectionBounds = { top: number; bottom: number; contentTop: number; contentBottom: number };
type Destination = { index: number; align: "center" | "start" | "end" } | { top: number };

const topInset = 96;
const bottomInset = 24;
const scrollIdleMs = 320;
const gestureGapMs = 450;

function measureDentalSection(section: HTMLElement, scrollTop: number): SectionBounds {
  const rect = section.getBoundingClientRect();
  const content = section.querySelector<HTMLElement>(":scope > :not([aria-hidden]):not(.pointer-events-none)");
  const contentRect = content?.getBoundingClientRect() ?? rect;
  const translation = parseFloat(content?.style.translate.split(" ")[1] ?? "0") || 0;
  return {
    top: rect.top + scrollTop, bottom: rect.bottom + scrollTop,
    contentTop: contentRect.top + scrollTop - translation,
    contentBottom: contentRect.bottom + scrollTop - translation,
  };
}

export function getDentalSectionAnchorTop(section: HTMLElement, scrollTop: number, height: number) {
  const bounds = measureDentalSection(section, scrollTop);
  const fits = bounds.contentBottom - bounds.contentTop <= height - topInset - bottomInset;
  return resolveDentalSectionDestination({ index: 0, align: fits ? "center" : "start" }, [bounds], height);
}

export function resolveDentalSectionDestination(destination: Destination, sections: SectionBounds[], height: number) {
  if ("top" in destination) return destination.top;
  const section = sections[destination.index];
  if (destination.align === "start") return section.contentTop - topInset;
  if (destination.align === "end") return section.contentBottom - height + bottomInset;
  return (section.contentTop + section.contentBottom - height - topInset + bottomInset) / 2;
}

export function getDentalSectionDestination(
  sections: SectionBounds[], scrollTop: number, height: number, direction: number,
): Destination | null {
  if (!sections.length || !direction) return null;
  const available = height - topInset - bottomInset;
  // Reading within a long section remains entirely native, without paging steps.
  if (sections.some((section) => section.contentBottom - section.contentTop > available + 32
    && scrollTop >= section.contentTop - topInset - 8
    && scrollTop <= section.contentBottom - height + bottomInset + 8)) return null;

  const candidates = sections.flatMap((section, index) => {
    const visible = Math.min(section.contentBottom, scrollTop + height) - Math.max(section.contentTop, scrollTop);
    if (visible < Math.min(height, section.contentBottom - section.contentTop) * 0.15) return [];
    const destination: Destination = index === 0 ? { top: 0 } : {
      index,
      align: section.contentBottom - section.contentTop <= available + 32 ? "center" : direction > 0 ? "start" : "end",
    };
    const distance = (resolveDentalSectionDestination(destination, sections, height) - scrollTop) * Math.sign(direction);
    // Help finish entering a visible section, never pull back or jump a full page.
    return distance > 8 && distance <= height * 0.85 ? [{ destination, distance }] : [];
  });
  candidates.sort((a, b) => a.distance - b.distance);
  return candidates[0]?.destination ?? null;
}

export function installDentalSectionNavigation(main: HTMLElement) {
  const document = main.ownerDocument;
  const browser = document.defaultView;
  if (!browser) return () => {};
  const sections = Array.from(main.querySelectorAll<HTMLElement>(":scope > section"));
  const reducedMotion = browser.matchMedia("(prefers-reduced-motion: reduce)");
  let frame = 0;
  let idleTimer = 0;
  let lastScrollTop = browser.scrollY;
  let lastInputAt = -Infinity;
  let travel = 0;
  let armed = false;
  let interruptedGesture = false;
  let pointerHeld = false;
  let touchHeld = false;

  const measure = () => sections.map((section) => measureDentalSection(section, browser.scrollY));
  const cancel = () => {
    browser.clearTimeout(idleTimer);
    browser.cancelAnimationFrame(frame);
    idleTimer = 0;
    frame = 0;
  };
  const stop = () => { cancel(); armed = false; travel = 0; };
  const navigate = (destination: Destination) => {
    const start = browser.scrollY;
    const started = browser.performance.now();
    const tick = (now: number) => {
      if (reducedMotion.matches) { stop(); return; }
      const progress = Math.min(1, (now - started) / 520);
      const eased = 1 - (1 - progress) ** 3;
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - browser.innerHeight);
      const target = Math.max(0, Math.min(maxScroll, resolveDentalSectionDestination(destination, measure(), browser.innerHeight)));
      browser.scrollTo({ top: start + (target - start) * eased, behavior: "instant" });
      lastScrollTop = browser.scrollY;
      frame = progress < 1 ? browser.requestAnimationFrame(tick) : 0;
    };
    frame = browser.requestAnimationFrame(tick);
  };
  const settle = () => {
    idleTimer = 0;
    if (!armed || pointerHeld || touchHeld || reducedMotion.matches || Math.abs(travel) < 8) return;
    const destination = getDentalSectionDestination(measure(), browser.scrollY, browser.innerHeight, Math.sign(travel));
    armed = false;
    travel = 0;
    if (destination) navigate(destination);
  };
  const schedule = () => {
    browser.clearTimeout(idleTimer);
    if (armed && !pointerHeld && !touchHeld) idleTimer = browser.setTimeout(settle, scrollIdleMs);
  };
  const onScroll = () => {
    const delta = browser.scrollY - lastScrollTop;
    lastScrollTop = browser.scrollY;
    if (frame || !armed || Math.abs(delta) < 0.5) return;
    travel = Math.sign(travel) === Math.sign(delta) ? travel + delta : delta;
    schedule();
  };
  const nativeInteraction = (event: Event) => {
    let element = event.target as HTMLElement | null;
    if (!element?.closest) return true;
    if (!main.contains(element) && element !== document.body && element !== document.documentElement) return true;
    if (element.closest("input, textarea, select, [contenteditable=true], [role=dialog], [role=listbox]")) return true;
    while (element && element !== main && element !== document.body) {
      if (/auto|scroll/.test(browser.getComputedStyle(element).overflowY) && element.scrollHeight > element.clientHeight + 2) return true;
      element = element.parentElement;
    }
    return false;
  };
  const beginInput = (eligible: boolean) => {
    const now = browser.performance.now();
    if (now - lastInputAt > gestureGapMs) { travel = 0; interruptedGesture = false; }
    // If the user takes over, leave this entire gesture alone, including inertia.
    if (frame) interruptedGesture = true;
    cancel();
    lastInputAt = now;
    armed = eligible && !interruptedGesture;
  };
  const onWheel = (event: WheelEvent) => beginInput(!event.ctrlKey && Math.abs(event.deltaY) > Math.abs(event.deltaX) && !nativeInteraction(event));
  const onPointerDown = (event: PointerEvent) => { pointerHeld = true; beginInput(!nativeInteraction(event)); };
  const onPointerUp = () => { pointerHeld = false; schedule(); };
  const onTouchStart = (event: TouchEvent) => { touchHeld = true; beginInput(event.touches.length === 1 && !nativeInteraction(event)); };
  const onTouchMove = (event: TouchEvent) => {
    if (event.touches.length !== 1) stop();
    else { browser.clearTimeout(idleTimer); lastInputAt = browser.performance.now(); }
  };
  const onTouchEnd = (event: TouchEvent) => { touchHeld = event.touches.length > 0; schedule(); };
  const onKeyDown = (event: KeyboardEvent) => {
    const scrollKey = ["ArrowDown", "ArrowUp", "PageDown", "PageUp", " "].includes(event.key);
    beginInput(scrollKey && !event.ctrlKey && !event.metaKey && !nativeInteraction(event));
  };
  const onBlur = () => { pointerHeld = false; touchHeld = false; stop(); };
  const listeners = [
    ["scroll", onScroll], ["wheel", onWheel], ["pointerdown", onPointerDown],
    ["pointerup", onPointerUp], ["pointercancel", onPointerUp],
    ["touchstart", onTouchStart], ["touchmove", onTouchMove],
    ["touchend", onTouchEnd], ["touchcancel", onTouchEnd], ["keydown", onKeyDown],
    ["click", stop], ["resize", stop], ["pagehide", stop], ["hashchange", stop], ["blur", onBlur],
  ] as const;
  // Observe input only: native wheel, touch, keyboard and scrollbar behavior wins.
  listeners.forEach(([name, listener]) => browser.addEventListener(name, listener as EventListener, { passive: true }));
  return () => {
    stop();
    listeners.forEach(([name, listener]) => browser.removeEventListener(name, listener as EventListener));
  };
}
